// src/pages/tareas/renderers/PreguntaRenderer.js
import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  Lightbulb,
  SkipForward,
  Coins,
  Heart,
  AlertTriangle,
} from "lucide-react";
import { useCourseStructure } from "../../../context/CourseStructureContext";

/* ======================= ESTILOS (layout sin scroll innecesario) ======================= */
const Q_STYLES = (
  <style>{`
  :root{
    --q-bg:#ffffff; --q-ink:#0f172a; --q-dim:#64748b; --q-border:#e5e7eb;
    --q-primary:#4f46e5; --q-primary-2:#7c3aed; --q-danger:#dc2626; --q-warn:#f59e0b;
    --q-shadow:0 6px 22px rgba(0,0,0,.06); --q-shadow-lg:0 12px 40px rgba(0,0,0,.10);
    --q-radius:16px;
    --q-maxw: 1200px;
    --q-measure: clamp(70ch, 88vw, 115ch);
    --q-head-h: clamp(60px, 12vh, 120px);
    --q-footer-h: clamp(110px, 18vh, 150px);
  }

  .q-enter{ animation:q-enter .18s ease both; }
  @keyframes q-enter{ from{opacity:.0; transform: translateY(6px)} to{opacity:1; transform:none} }

  .q-shell{
    margin:0 auto;
    max-width:min(var(--q-maxw),98%);
    background:var(--q-bg);
    border:1px solid var(--q-border);
    border-radius:var(--q-radius);
    box-shadow:var(--q-shadow);
    color:var(--q-ink);
    overflow:clip;
    display:flex;
    flex-direction:column;
    min-height:70dvh;
  }

  .q-head{
    padding:14px 14px 10px;
    border-bottom:1px solid var(--q-border);
    background: radial-gradient(520px 220px at -20% -30%, rgba(79,70,229,.06), transparent 70%), var(--q-bg);
  }
  .q-head-inner{ 
    margin:0 auto; 
    max-width:var(--q-measure); 
    display:flex; 
    flex-direction:row;
    justify-content:space-between;
    align-items:center;
    gap:12px; 
  }
  .q-head-left{ flex:1; display:flex; flex-direction:column; gap:8px; }
  .q-title{ margin:0; font-weight:800; letter-spacing:.2px; font-size: clamp(1.02rem, 0.96rem + 0.6vw, 1.24rem); line-height:1.32; word-break:break-word; }
  .q-sub{ display:flex; flex-wrap:wrap; gap:8px; align-items:center; color:var(--q-dim); font-size:.82rem; font-weight:700; }

  .q-body{ padding:12px 14px 0; flex:1 0 auto; }
  .q-inner{ margin:0 auto; max-width:var(--q-measure); display:flex; flex-direction:column; gap:12px; min-height:0; }

  .q-stage{
    display:flex; flex-direction:column; gap:12px;
    min-height:auto; max-height:none;
    overflow:visible;
    padding-bottom:10px;
    flex:1 0 auto;
  }
  .q-stage.scrollable{
    overflow:auto;
    max-height: calc(100dvh - var(--q-head-h) - var(--q-footer-h) - 40px);
    scrollbar-gutter: stable both-edges;
  }

  .q-badge-danger{ display:inline-flex; align-items:center; gap:6px; border:1px solid; padding:4px 10px; border-radius:999px; font-weight:800; background:#fff1f2; color:#b91c1c; border-color:#fecaca; }

  .q-chip{ display:inline-flex; align-items:center; gap:8px; padding:6px 10px; border-radius:10px; border:1px solid var(--q-border); background:#fff; font-size:.86rem; font-weight:800; color:#0b1220; }
  .q-chip.warn{ background:#fef3c7; color:#92400e; border-color:#fde68a; }
  .q-chip.danger{ background:#fee2e2; color:#dc2626; border-color:#fecaca; }

  .q-btn{ display:inline-flex; align-items:center; gap:8px; padding:8px 12px; font-size:.9rem; font-weight:800; border-radius:10px; border:1px solid var(--q-border); background:#fff; color:#0f172a; cursor:pointer; line-height:1.2; transition:transform .12s, box-shadow .15s, background .15s; }
  .q-btn:hover{ transform: translateY(-1px); box-shadow: var(--q-shadow); background:#f8fafc; }
  .q-btn[disabled]{ opacity:.5; cursor:not-allowed; transform:none; }

  .q-media{ margin:4px 0 6px; }
  .q-media figure{ margin:0; }
  .q-media img{ width:100%; max-height:320px; object-fit:contain; border-radius:10px; border:1px solid var(--q-border); box-shadow:0 6px 18px rgba(0,0,0,.06); background:#fff; }
  .q-media figcaption{ font-size:12px; color:#6b7280; margin-top:4px; }

  .q-input{
    border:1px solid var(--q-border); border-radius:12px; background:#fff; color:var(--q-ink);
    box-shadow:0 1px 2px rgba(0,0,0,.04) inset; transition: box-shadow .15s, border-color .15s, background .15s;
    font-size:1rem; line-height:1.5; padding:10px 12px;
  }
  .q-input--short{ width:auto; min-width: clamp(12ch, 35vw, 40ch); max-width: min(100%, 56ch); }
  .q-inline-input{ width:auto; min-width:8ch; max-width:28ch; display:inline-block; vertical-align:baseline; }

  .q-optStack{ display:flex; flex-direction:column; gap:10px; }
  .q-optRow{ width:100%; min-height:40px; display:flex; align-items:center; gap:12px; padding:10px 12px; border-radius:12px; border:1px solid var(--q-border); background:#fff; transition: background .15s, border-color .15s, transform .05s; }
  .q-optRow:hover{ background:#f8fafc; }
  .q-optRow.selected{ background:#eef2ff; border-color:#c7d2fe; }
  .q-optRow input{ accent-color: var(--q-primary); }
  .q-optLabel{ display:block; flex:1; font-size:.95rem; line-height:1.45; word-break:break-word; }

  .q-optGrid{ display:grid; grid-template-columns: repeat(auto-fit, minmax(144px, 1fr)); gap:10px; }
  .q-pill{ min-height:40px; display:flex; align-items:center; justify-content:center; text-align:center; padding:8px 10px; border-radius:12px; border:1px solid var(--q-border); background:#fff; font-weight:800; font-size:.94rem; transition: background .15s, border-color .15s, transform .05s; cursor:pointer; }
  .q-pill.selected{ background:#eef2ff; border-color:#c7d2fe; }

  .q-alert{ display:flex; flex-wrap:wrap; gap:10px; align-items:flex-start; padding:12px 14px; border:1px solid var(--q-border); border-radius:12px; background:#fff; font-size:.95rem; }
  .q-alert.success{ background:#ecfdf5; border-color:#bbf7d0; color:#065f46; }

  .q-ordList{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:10px; }
  .q-ordRow{ display:flex; flex-wrap:wrap; align-items:center; gap:10px; }
  .q-ordIndex{ background:#eef2ff; color:#3730a3; padding:4px 8px; border-radius:8px; font-size:.8rem; font-weight:800; min-width:28px; text-align:center; }
  .q-ordBox{ flex:1; min-width:0; border:1px solid var(--q-border); border-radius:10px; background:#fff; padding:12px; font-size:1rem; }
  .q-ordCtl{ display:flex; gap:8px; }
  .q-ordBtn{ padding:8px 12px; border-radius:10px; border:1px solid var(--q-border); background:#fff; font-size:.9rem; cursor:pointer; min-width:36px; }

  .q-pairGrid{ display:grid; grid-template-columns:1fr 1fr; gap:12px; align-items:start; }
  .q-pairHead{ font-size:.9rem; font-weight:800; color:#1f2937; }
  .q-pairLeft{ padding:12px; border:1px solid var(--q-border); border-radius:10px; background:#f8fafc; font-size:1rem; color:#1f2937; }
  .q-pairSelect select{ width:100%; padding:8px 10px; border-radius:10px; border:1px solid var(--q-border); background:#fff; font-size:1rem; color:#1f2937; }
  @media(max-width:560px){ .q-pairGrid{ grid-template-columns:1fr; } }

  /* ================= Footer (acciones + comodines inline + vidas debajo) ================= */
  .q-footer{
    position: sticky; bottom:-1px; z-index:3; margin-top:14px;
    background: linear-gradient(180deg, rgba(255,255,255,.86), rgba(255,255,255,.98) 40%, rgba(255,255,255,1) 100%);
    backdrop-filter: blur(8px); border-top:1px solid var(--q-border);
    padding: 10px 10px calc(10px + env(safe-area-inset-bottom));
  }

  .q-actions{
    display:flex;
    flex-wrap:wrap;
    gap:10px;
    align-items:center;
  }

  .q-actions-right{
    display:flex;
    align-items:center;
    gap:10px;
    margin-left:auto;
  }

  .q-primary{
    padding:11px 18px; border-radius:12px; border:0; font-size:.95rem; font-weight:900; cursor:pointer;
    background: linear-gradient(90deg,var(--q-primary),var(--q-primary-2)); color:#fff;
    box-shadow:0 10px 24px rgba(99,102,241,.18);
  }
  .q-pad-bottom{ padding-bottom:8px; }

  /* Vidas: compactas en header */
  .q-lives-compact{
    display:inline-flex;
    align-items:center;
    gap:6px;
    padding:6px 10px;
    border:1px solid var(--q-border);
    border-radius:999px;
    background: radial-gradient(180px 80px at 30% -40%, rgba(220,38,38,.08), transparent 70%), #fff;
    box-shadow: 0 4px 12px rgba(0,0,0,.04);
  }
  .q-heart{
    width:24px; height:24px;
    display:inline-flex;
    align-items:center;
    justify-content:center;
    border-radius:999px;
    border:1px solid #fecaca;
    background:#fff1f2;
    color:#dc2626;
    transition: background .18s ease, filter .18s ease, opacity .18s ease, border-color .18s ease, color .18s ease;
  }
  .q-heart.off{
    background:#f8fafc;
    color:#94a3b8;
    border-color:#e5e7eb;
    opacity:.8;
    filter: grayscale(1);
  }
  .q-heart-anim{ 
    animation: qLifePulse 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) !important; 
  }
  @keyframes qLifePulse{ 
    0%{ transform: scale(1); } 
    50%{ transform: scale(1.5); filter: brightness(1.2); } 
    100%{ transform: scale(1); } 
  }

  /* ===== Comodines inline expandibles ===== */
  .q-comodWrap{ position:relative; display:inline-flex; align-items:center; }
  .q-comodBtn{
    display:inline-flex; align-items:center; gap:10px;
    padding:9px 12px;
    border-radius:12px;
    border:1px solid var(--q-border);
    background:#ffffff;
    font-weight:900;
    cursor:pointer;
    box-shadow: 0 10px 20px rgba(0,0,0,.06);
    transition: transform .12s ease, box-shadow .15s ease, background .15s ease;
    z-index:2;
  }
  .q-comodBtn:hover{ transform: translateY(-1px); box-shadow: var(--q-shadow-lg); background:#f8fafc; }
  .q-comodBtn[disabled]{ opacity:.55; cursor:not-allowed; transform:none; box-shadow:none; }
  .q-comodBtn.cta{
    border-color: rgba(99,102,241,.65);
    box-shadow: 0 0 0 6px rgba(99,102,241,.14), 0 18px 40px rgba(99,102,241,.18);
    animation: qCtaGlow 1.1s ease-in-out infinite;
  }
  @keyframes qCtaGlow{
    0%,100%{ transform: translateY(0); filter: brightness(1); }
    50%{ transform: translateY(-1px); filter: brightness(1.04); }
  }

  .q-comodOptions{
    display:flex;
    gap:8px;
    margin-left:8px;
    animation: qSlideIn .25s cubic-bezier(0.34, 1.56, 0.64, 1);
  }
  @keyframes qSlideIn{
    from{ opacity:0; transform: translateX(-10px) scale(0.95); }
    to{ opacity:1; transform: translateX(0) scale(1); }
  }
  .q-comodOption{
    display:inline-flex; align-items:center; gap:8px;
    padding:9px 14px;
    border-radius:12px;
    border:1px solid var(--q-border);
    background:#fff;
    font-weight:800;
    font-size:.88rem;
    cursor:pointer;
    box-shadow: 0 4px 12px rgba(0,0,0,.08);
    transition: all .15s ease;
    white-space:nowrap;
  }
  .q-comodOption:hover:not([disabled]){ transform: translateY(-2px) scale(1.02); box-shadow: 0 8px 20px rgba(0,0,0,.12); background:#f8fafc; }
  .q-comodOption[disabled]{ opacity:.4; cursor:not-allowed; filter: grayscale(0.8); }

  /* ===== Modales (solo los locales: saltar/incorrect) ===== */
  .q-modalWrap{ position:fixed; inset:0; background:rgba(15,23,42,.52); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; }
  .q-modal{ background:#fff; border-radius:18px; padding:0; width:min(560px,100%); box-shadow:var(--q-shadow-lg); overflow:hidden; text-align:center; }

  .q-modalHead{
    padding:12px 16px;
    font-weight:1000;
    color:#fff;
    background: linear-gradient(90deg, var(--q-primary), var(--q-primary-2));
    letter-spacing:.4px;
  }
  .q-modalBody{ padding:18px 18px 16px; }
  .q-modalIcon{
    width:66px; height:66px; border-radius:999px;
    margin: 6px auto 12px;
    display:flex; align-items:center; justify-content:center;
    background: radial-gradient(180px 120px at 50% -20%, rgba(220,38,38,.18), transparent 60%), #fff1f2;
    border: 1px solid #fecaca;
    color:#dc2626;
    box-shadow: 0 10px 24px rgba(220,38,38,.12);
  }
  .q-modalTitle{ font-size:1.12rem; font-weight:1000; color:#0f172a; margin:0 0 8px; }
  .q-modalText{ color:#475569; margin:0 0 14px; line-height:1.55; font-weight:700; }

  .q-modalNote{
    margin: 10px auto 14px;
    width: min(520px, 100%);
    background:#eef2ff;
    border:1px solid #c7d2fe;
    color:#3730a3;
    padding:12px 12px;
    border-radius:14px;
    font-weight:800;
    line-height:1.45;
  }

  .q-modalBtns{ display:flex; gap:10px; justify-content:center; flex-wrap:wrap; padding: 0 18px 18px; }
  .q-modalBtn{
    border-radius:12px;
    padding:10px 14px;
    font-weight:1000;
    border:1px solid var(--q-border);
    background:#fff;
    cursor:pointer;
  }
  .q-modalBtn.primary{
    border:0;
    color:#fff;
    background: linear-gradient(90deg,var(--q-primary),var(--q-primary-2));
    box-shadow:0 10px 24px rgba(99,102,241,.18);
  }
  .q-modalBtn.danger{
    border:0;
    color:#fff;
    background:#ef4444;
    box-shadow:0 10px 24px rgba(239,68,68,.18);
  }
  `}</style>
);

/* ======================= HELPERS ======================= */
const normalize = (s = "") =>
  String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
const arraysEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

function calcularPuntaje(intentosEfectivos, pistas, puntosBase) {
  const factorIntento = intentosEfectivos <= 1 ? 1 : intentosEfectivos === 2 ? 0.7 : 0.5;
  const penalizacionPistas = pistas * 0.2;
  const factor = clamp(factorIntento - penalizacionPistas, 0, 1);
  return Math.round(puntosBase * factor * 100) / 100;
}

function pick5050AllowedIds(question) {
  const opciones = (question?.opciones?.opciones || []).map((o) => ({
    id: String(o.id),
    texto: o.texto,
  }));
  if (opciones.length < 4) return null;

  const correctas = (question?.respuesta_correcta?.correctas || []).map(String);
  const correcta =
    question?.respuesta_correcta?.correcta != null
      ? String(question.respuesta_correcta.correcta)
      : null;

  const correctId = correctas?.length ? correctas[0] : correcta;
  if (!correctId) return null;

  const wrongPool = opciones.map((o) => o.id).filter((id) => id !== String(correctId));
  if (!wrongPool.length) return null;

  const wrongId = wrongPool[Math.floor(Math.random() * wrongPool.length)];
  return [String(correctId), String(wrongId)];
}

/* ======================= Evaluación ======================= */
function evaluateAnswer(tipo, question, answer) {
  switch (tipo) {
    case "respuesta_corta": {
      const correct = question?.respuesta_correcta?.respuesta ?? "";
      return normalize(answer) === normalize(correct);
    }
    case "seleccion_multiple": {
      const correctIds = (question?.respuesta_correcta?.correctas || []).map(String).sort();
      const chosen = (answer || []).map(String).sort();
      return arraysEqual(correctIds, chosen);
    }
    case "verdadero_falso": {
      const correct = String(question?.respuesta_correcta?.respuesta || "").toLowerCase();
      const chosen = String((answer && answer[0]) || "").toLowerCase();
      return chosen === correct;
    }
    case "completar": {
      const correct = question?.respuesta_correcta?.respuestas || [];
      const user = answer || [];
      if (user.length !== correct.length) return false;
      return user.every((v, i) => normalize(v) === normalize(correct[i] ?? ""));
    }
    case "ordenamiento": {
      const correctOrder = (question?.respuesta_correcta?.orden_correcto || []).map(String);
      const userOrder = (answer || []).map(String);
      return arraysEqual(correctOrder, userOrder);
    }
    case "emparejamiento": {
      const correctPairs = (question?.respuesta_correcta?.pares_correctos || []).map((p) => ({
        a: String(p.a),
        b: String(p.b),
      }));
      const userPairs = Array.isArray(answer)
        ? answer.map((p) => ({ a: String(p.a), b: String(p.b) }))
        : Object.entries(answer || {}).map(([a, b]) => ({ a: String(a), b: String(b) }));

      if (userPairs.length !== correctPairs.length) return false;

      const key = (p) => `${p.a}->${p.b}`;
      const setA = new Set(userPairs.map(key));
      const setB = new Set(correctPairs.map(key));
      if (setA.size !== setB.size) return false;
      for (const k of setA) if (!setB.has(k)) return false;
      return true;
    }
    default:
      return false;
  }
}

/* ======================= Respuestas iniciales ======================= */
function blanksCountFor(question) {
  const texto = question?.opciones?.texto_con_espacios || "";
  const blocks = texto.split("_____");
  const provided = question?.opciones?.numero_espacios || 0;
  const desdeTexto = Math.max(0, blocks.length - 1);
  const desdeRespuesta = (question?.respuesta_correcta?.respuestas || []).length;
  return Math.max(provided, desdeTexto, desdeRespuesta);
}

// helper seguro
const safeJson = (v, fallback = null) => {
  if (v == null) return fallback;
  if (typeof v !== "string") return v; // ya es objeto/array
  try {
    return JSON.parse(v);
  } catch {
    return fallback;
  }
};

const extractOptionsList = (qq) => {
  const opRaw = safeJson(qq?.opciones, qq?.opciones);

  let list =
    Array.isArray(opRaw) ? opRaw :
      Array.isArray(opRaw?.opciones) ? opRaw.opciones :
        Array.isArray(opRaw?.options) ? opRaw.options :
          Array.isArray(opRaw?.items) ? opRaw.items :
            [];

  return list.map((it, idx) => {
    if (it == null) return { id: String(idx + 1), texto: "" };
    if (typeof it === "string") return { id: String(idx + 1), texto: it };
    const id = it?.id ?? it?.value ?? (idx + 1);
    const texto = it?.texto ?? it?.label ?? String(it?.value ?? "");
    return { id: String(id), texto };
  });
};

const hashStableQuestionSeed = (value) => {
  const input = String(value ?? "");
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

const shuffleQuestionOptionsForRender = (question) => {
  const tipo = String(question?.tipo_pregunta ?? question?.tipo ?? "").toLowerCase();
  if (tipo !== "seleccion_multiple") return question;

  const optionList = Array.isArray(question?.opciones?.opciones)
    ? question.opciones.opciones
    : [];

  if (optionList.length < 2) return question;

  const baseSeed = [
    question?.id,
    question?.uid,
    question?.pregunta,
    question?.nombre,
  ].filter(Boolean).join("::");

  const shuffled = optionList
    .map((option, index) => {
      const optionKey = String(option?.id ?? option?.value ?? option?.texto ?? index);
      return {
        option,
        index,
        order: hashStableQuestionSeed(baseSeed + "::" + optionKey + "::" + index),
      };
    })
    .sort((a, b) => a.order - b.order || a.index - b.index)
    .map(({ option }) => option);

  return {
    ...question,
    opciones: {
      ...question.opciones,
      opciones: shuffled,
    },
  };
};

function initialAnswerFor(tipo, question) {
  switch (tipo) {
    case "respuesta_corta":
      return "";
    case "seleccion_multiple":
      return [];
    case "verdadero_falso":
      return [];
    case "completar":
      return Array.from({ length: blanksCountFor(question) }, () => "");
    case "ordenamiento": {
      const opciones = extractOptionsList(question);
      return opciones.map((o) => String(o.id));
    }
    case "emparejamiento": {
      const colA = question?.opciones?.columna_a || [];
      return Object.fromEntries(colA.map((a) => [String(a.id), ""]));
    }
    default:
      return null;
  }
}

function isComplete(tipo, question, answer) {
  switch (tipo) {
    case "respuesta_corta":
      return Boolean(String(answer || "").trim());
    case "seleccion_multiple":
      return Array.isArray(answer) && answer.length > 0;
    case "verdadero_falso":
      return Array.isArray(answer) && answer.length === 1;
    case "completar":
      return (
        Array.isArray(answer) &&
        answer.length === blanksCountFor(question) &&
        answer.every((a) => String(a).trim())
      );
    case "ordenamiento":
      return Array.isArray(answer) && answer.length >= 1;
    case "emparejamiento": {
      const values = Array.isArray(answer) ? answer.map((p) => p.b) : Object.values(answer || {});
      return values.length > 0 && values.every((v) => v !== "" && v != null);
    }
    default:
      return false;
  }
}

/* ======================= REDUCER ======================= */
const INIT = "INIT";
const ANSWER_CHANGE = "ANSWER_CHANGE";
const SHOW_HINT = "SHOW_HINT";
const LOCK = "LOCK";
const MODAL = "MODAL";
const ATTEMPT = "ATTEMPT";
const SET_5050 = "SET_5050";
const CLEAR_5050 = "CLEAR_5050";

function reducer(state, action) {
  switch (action.type) {
    case INIT:
      return {
        ...state,
        questionKey: action.questionKey,
        tipo: action.tipo,
        puntos: action.puntos,
        hintText: action.hintText,
        hasExplicacion: action.hasExplicacion,
        media: action.media,
        intentos: 0,
        pistas: 0,
        mostrarPista: false,
        locked: false,
        showRetry: false,
        fiftyAllowed: null,
        fiftyUsed: false,
        answer: action.initialAnswer,
        complete: isComplete(action.tipo, action.question, action.initialAnswer),
        modals: {
          saltarBloqueado: false,
          incorrect: false,
        },
      };

    case ANSWER_CHANGE: {
      const newComplete = isComplete(state.tipo, action.question, action.value);
      const changed = JSON.stringify(action.value) !== JSON.stringify(state.answer);
      return {
        ...state,
        answer: action.value,
        complete: newComplete,
        locked: changed ? false : state.locked,
        showRetry: changed ? false : state.showRetry,
        modals: changed ? { ...state.modals, incorrect: false } : state.modals,
      };
    }

    case SHOW_HINT:
      return {
        ...state,
        mostrarPista: true,
        pistas: state.pistas + 1,
        locked: false,
        showRetry: false,
      };

    case LOCK:
      return { ...state, locked: true, showRetry: true };

    case MODAL:
      return { ...state, modals: { ...state.modals, [action.name]: action.open } };

    case ATTEMPT:
      return { ...state, intentos: (state.intentos || 0) + 1 };

    case SET_5050: {
      const allowed = Array.isArray(action.allowedIds) ? action.allowedIds.map(String) : null;
      if (!allowed) return state;

      const current = Array.isArray(state.answer) ? state.answer.map(String) : [];
      const filtered = current.filter((id) => allowed.includes(String(id)));

      return {
        ...state,
        fiftyAllowed: allowed,
        fiftyUsed: true,
        answer: filtered,
        complete: isComplete(state.tipo, action.question, filtered),
      };
    }

    case CLEAR_5050:
      return { ...state, fiftyAllowed: null };

    default:
      return state;
  }
}

function usePreguntaReducer(seed) {
  return useReducer(reducer, { ...seed, modals: { saltarBloqueado: false, incorrect: false } });
}

/* ======================= UI helpers ======================= */
function Media({ media, toAbsoluteUrl }) {
  if (!media?.type || !media?.url) return null;
  const abs = toAbsoluteUrl(media.url);
  if (!abs) return null;

  if (media.type === "image") {
    return (
      <div className="q-media">
        <figure>
          <img src={abs} alt={media.alt || "Imagen"} />
          {media.caption ? <figcaption>{media.caption}</figcaption> : null}
        </figure>
      </div>
    );
  }
  if (media.type === "audio") {
    return (
      <div className="q-media">
        <figure>
          <audio controls src={abs} style={{ width: "100%" }} />
          {media.caption ? <figcaption>{media.caption}</figcaption> : null}
        </figure>
      </div>
    );
  }
  return null;
}

const BaseModal = ({ open, children }) =>
  !open ? null : (
    <div className="q-modalWrap">
      <div className="q-modal">{children}</div>
    </div>
  );

function LivesCompact({ lives = 3, animKey = 0, clearForcedSignal = 0 }) {
  const safe = Math.max(0, Math.min(3, Number(lives) || 0));
  const prevSafeRef = useRef(safe);
  const prevAnimKeyRef = useRef(animKey);
  const prevClearSignalRef = useRef(clearForcedSignal);
  const [animatingIndex, setAnimatingIndex] = useState(null);
  const [forcedOnIndex, setForcedOnIndex] = useState(null);

  useEffect(() => {
    const prevSafe = prevSafeRef.current;
    const prevAnimKey = prevAnimKeyRef.current;
    const prevClearSignal = prevClearSignalRef.current;

    const animKeyChanged = animKey !== prevAnimKey;
    const livesChanged = safe !== prevSafe;
    const clearSignalChanged = clearForcedSignal !== prevClearSignal;

    if (clearSignalChanged && clearForcedSignal > 0) {
      setForcedOnIndex(null);
      setAnimatingIndex(0);
      setTimeout(() => setAnimatingIndex(null), 600);
    } else if (livesChanged) {
      if (safe > prevSafe) {
        const recoveredIndex = prevSafe;
        setAnimatingIndex(recoveredIndex);
        setTimeout(() => setAnimatingIndex(null), 600);
      } else if (safe < prevSafe) {
        const lostIndex = safe;
        setAnimatingIndex(lostIndex);
        setTimeout(() => setAnimatingIndex(null), 600);
      }
    } else if (animKeyChanged && safe === 0 && prevSafe === 0) {
      setAnimatingIndex(0);
      setForcedOnIndex(0);
      setTimeout(() => setAnimatingIndex(null), 600);
    }

    prevSafeRef.current = safe;
    prevAnimKeyRef.current = animKey;
    prevClearSignalRef.current = clearForcedSignal;
  }, [safe, animKey, clearForcedSignal]);

  return (
    <div className="q-lives-compact" aria-label={`Vidas: ${safe}/3`}>
      {[0, 1, 2].map((i) => {
        const naturallyOn = i < safe;
        const forceOn = i === forcedOnIndex;
        const shouldAnimate = i === animatingIndex;
        const isOn = naturallyOn || forceOn;

        return (
          <span
            key={i}
            className={`q-heart ${isOn ? "on" : "off"} ${shouldAnimate ? "q-heart-anim" : ""}`}
            title={isOn ? "Vida" : "Vida perdida"}
          >
            <Heart size={14} />
          </span>
        );
      })}
    </div>
  );
}

function ComodinesInline({ open, comodines, allowHint, showRetry, onPista, onReintentar }) {
  if (!open) return null;
  const sinComodines = comodines <= 0;

  return (
    <div className="q-comodOptions">
      <button
        type="button"
        className="q-comodOption"
        onClick={onReintentar}
        disabled={sinComodines || !showRetry}
        title={showRetry ? "Recupera una vida" : "Disponible tras perder todas las vidas"}
      >
        <Heart size={16} />
        Vida extra
      </button>

      <button
        type="button"
        className="q-comodOption"
        onClick={onPista}
        disabled={sinComodines || !allowHint}
        title={allowHint ? "Muestra una pista" : "Esta pregunta no tiene pista"}
      >
        <Lightbulb size={16} />
        Pista
      </button>
    </div>
  );
}

/* ======================= COMPONENTE ======================= */
export default function PreguntaRenderer({ question, onResult, controls, openComodinesSignal = 0 }) {
  const { toAbsoluteUrl } = useCourseStructure();

  const q = useMemo(() => {
    const base = question || {};

    let opciones = safeJson(base.opciones, base.opciones);
    let respuesta_correcta = safeJson(base.respuesta_correcta, base.respuesta_correcta);

    if (Array.isArray(opciones)) opciones = { opciones };
    if (opciones == null || typeof opciones !== "object") opciones = {};
    if (respuesta_correcta == null || typeof respuesta_correcta !== "object") respuesta_correcta = {};

    if (!Array.isArray(opciones.opciones)) {
      if (Array.isArray(opciones.options)) opciones.opciones = opciones.options;
      else if (Array.isArray(opciones.items)) opciones.opciones = opciones.items;
      else opciones.opciones = [];
    }

    const tipo_pregunta =
      base?.tipo_pregunta ??
      base?.tipo ??
      base?.question_type ??
      "respuesta_corta";

    return shuffleQuestionOptionsForRender({ ...base, tipo_pregunta, opciones, respuesta_correcta });
  }, [question]);

  const tipo = q?.tipo_pregunta || "respuesta_corta";
  const titulo = q?.pregunta || q?.nombre || "Pregunta";
  const puntos = Number(q?.puntos ?? 1) || 1;

  const hintText =
    q?.pista ||
    q?.opciones?.pista ||
    q?.explicacion ||
    q?.respuesta_correcta?.explicacion ||
    "";

  const hasExplicacion = Boolean(String(hintText || "").trim());
  const media = q?.media || q?.opciones?.media || null;
  const questionKey = `${q?.id || "q"}::${tipo}`;

  const { estado, consume } = controls;
  const comodines = estado.comodines;
  const sinGanancias = estado.sinGanancias;
  const bloqueadoGlobal = estado.bloqueadoGlobal;
  const vidas = Number(estado?.vidas ?? 3);

  const [state, dispatch] = usePreguntaReducer({});
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    dispatch({
      type: INIT,
      questionKey,
      tipo,
      puntos,
      hintText,
      hasExplicacion,
      media,
      question: q,
      initialAnswer: initialAnswerFor(tipo, q),
    });
    setMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey]);

  // ✅ Señal del padre para abrir el menú REAL de comodines
  useEffect(() => {
    if (!openComodinesSignal) return;
    setMenuOpen(true);
  }, [openComodinesSignal]);

  // cerrar menú al click afuera
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  /* === Heurística: solo activar scroll del stage si hace falta === */
  const needsScroll = useMemo(() => {
    const optsLen = extractOptionsList(q).length;
    const colALen = q?.opciones?.columna_a?.length || 0;
    const colBLen = q?.opciones?.columna_b?.length || 0;
    const textoLen = (q?.opciones?.texto_con_espacios || "").length;
    const hasMedia = !!media;

    if (tipo === "seleccion_multiple" && optsLen > 8) return true;
    if (tipo === "emparejamiento" && (colALen > 6 || colBLen > 6)) return true;
    if (tipo === "ordenamiento" && optsLen > 7) return true;
    if (tipo === "completar" && textoLen > 500) return true;
    if (hasMedia) return true;
    return false;
  }, [tipo, q, media]);

  // Usar una clave única en sessionStorage para persistir entre preguntas
  const RETRY_SIGNAL_KEY = "pregunta_retry_signal";

  const [retrySignal, setRetrySignal] = useState(0);
  const [clearForcedSignal, setClearForcedSignal] = useState(0);

  // Cargar retrySignal desde sessionStorage al montar o cambiar de pregunta
  useEffect(() => {
    const stored = sessionStorage.getItem(RETRY_SIGNAL_KEY);
    if (stored && vidas === 0) {
      const storedSignal = parseInt(stored, 10);
      setRetrySignal(storedSignal);
    } else if (vidas > 0 && stored) {
      // Limpiar solo si las vidas subieron naturalmente (no vida extra)
      sessionStorage.removeItem(RETRY_SIGNAL_KEY);
      setRetrySignal(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey]);

  const canUse5050 = useMemo(() => {
    if (tipo !== "seleccion_multiple") return false;

    const opciones = extractOptionsList(q);
    const correctas = (q?.respuesta_correcta?.correctas || []).map(String);
    const isSingle = correctas.length === 1; // 50/50 solo opción única
    return isSingle && opciones.length >= 4;
  }, [tipo, q]);

  async function pedirPista() {
    if (!hasExplicacion) return;
    setMenuOpen(false);
    controls?.setCtaComodines?.(false);
    await consume("pista");
    dispatch({ type: SHOW_HINT });
  }

  async function reintentar() {
    setMenuOpen(false);
    controls?.setCtaComodines?.(false);

    const signal = Date.now();
    sessionStorage.setItem(RETRY_SIGNAL_KEY, String(signal)); // 🎯 Guardar en storage
    setRetrySignal(signal); // 🎯 Señal de vida extra

    const res = await controls.retry();

    if (res?.ok) {
      dispatch({ type: MODAL, name: "incorrect", open: false });

      dispatch({
        type: INIT,
        questionKey: `${questionKey}::retry::${Date.now()}`,
        tipo,
        puntos,
        hintText,
        hasExplicacion,
        media,
        question: q,
        initialAnswer: initialAnswerFor(tipo, q),
      });
    }
  }

  async function aplicar5050() {
    setMenuOpen(false);
    if (!canUse5050 || state.fiftyUsed) return;

    const allowed = pick5050AllowedIds(q);
    if (!allowed) return;

    controls?.setCtaComodines?.(false);

    const res = await consume("5050");
    if (res?.ok === false) return;

    dispatch({ type: SET_5050, allowedIds: allowed, question: q });
  }

  function handleSaltar() {
    if (state.locked && comodines > 0) {
      dispatch({ type: MODAL, name: "saltarBloqueado", open: true });
      return;
    }

    if (vidas === 0 && retrySignal > 0) {
      controls.skip({ locked: state.locked, keepRetrySignal: true });
    } else {
      controls.skip({ locked: state.locked });
    }
  }

  function confirmarSaltar() {
    dispatch({ type: MODAL, name: "saltarBloqueado", open: false });

    if (vidas === 0 && retrySignal > 0) {
      controls.skip({ locked: true, confirmed: true, keepRetrySignal: true });
    } else {
      controls.skip({ locked: true, confirmed: true });
    }
  }

  function handleCheck() {
    if (!state.complete) return;

    // Si está locked y NO hay vidas -> no mostramos incorrect
    if (state.locked) {
      const v = Number(controls?.estado?.vidas ?? 0);
      if (v <= 0) return;
      dispatch({ type: MODAL, name: "incorrect", open: true });
      return;
    }

    const correcto = evaluateAnswer(tipo, q, state.answer);
    dispatch({ type: ATTEMPT });

    const vidasActuales = Number(controls?.estado?.vidas ?? 0);
    const vidasLuegoDelFallo = correcto ? vidasActuales : vidasActuales - 1;

    if (!correcto) {
      // 🎯 Si fallamos con 0 vidas (después de vida extra), limpiar ANTES
      if (vidasActuales === 0) {
        sessionStorage.removeItem(RETRY_SIGNAL_KEY); // 🎯 Limpiar storage
        setClearForcedSignal(Date.now());
      }

      if (vidasLuegoDelFallo <= 0) {
        dispatch({ type: LOCK });
        dispatch({ type: MODAL, name: "incorrect", open: false });
      } else {
        dispatch({ type: MODAL, name: "incorrect", open: true });
      }
    } else {
      dispatch({ type: MODAL, name: "incorrect", open: false });
    }

    const intentos = (state.intentos || 0) + 1;
    const puntos_obtenidos =
      correcto && !sinGanancias ? calcularPuntaje(intentos, state.pistas, puntos) : 0;

    const meta = {
      explicacion: hasExplicacion ? hintText : "",
      scoring: {
        correcto,
        intentos,
        pistas_usadas: state.pistas,
        tiempo_usado: null,
        puntos_base: puntos,
        puntos_obtenidos,
        por_tiempo: false,
        sin_ganancias: sinGanancias,
      },
      comodines_restantes: comodines,
      sin_comodines: comodines <= 0,
      puede_ganar_coins: !sinGanancias && correcto,
      bloqueado_global: bloqueadoGlobal,
      tipo_pregunta: q?.tipo_pregunta || "",
      vidas_luego_del_fallo: vidasLuegoDelFallo,
    };

    onResult?.({ correct: correcto, answer: state.answer, meta });
  }

  const sinComodines = comodines <= 0;
  const ctaGlow = !!estado?.ctaComodines && !sinComodines;

  return (
    <>
      {Q_STYLES}
      <div className="q-shell q-enter">
        <div className="q-head">
          <div className="q-head-inner">
            <div className="q-head-left">
              <h3 className="q-title">{titulo}</h3>
              {(() => {
                const correctas = q?.respuesta_correcta?.correctas;
                const esMultiple = Array.isArray(correctas) && correctas.length > 1;
                const labels = {
                  seleccion_multiple: esMultiple ? "Selección múltiple" : "Selección simple",
                  verdadero_falso: "Verdadero o falso",
                  respuesta_corta: "Respuesta corta",
                  completar: "Completar",
                  emparejamiento: "Emparejamiento",
                  ordenar: "Ordenar",
                };
                const label = labels[tipo] || tipo.replace(/_/g, " ");
               return (
                  <span style={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    color: "#6366f1",
                    background: "#eef2ff",
                    border: "1px solid #c7d2fe",
                    borderRadius: "999px",
                    padding: "3px 12px",
                    marginTop: "6px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "5px",
                    letterSpacing: ".06em",
                    textTransform: "uppercase",
                    width: "fit-content",
                    boxShadow: "0 1px 4px rgba(99,102,241,.12)",
                  }}>
                    ✦ {label}
                  </span>
                );
              })()}
            </div>

            <LivesCompact
              lives={vidas}
              animKey={retrySignal}
              clearForcedSignal={clearForcedSignal}
            />
          </div>
        </div>

        <div className="q-body">
          <div className="q-inner">
            <div className={`q-stage ${needsScroll ? "scrollable" : ""}`}>
              {bloqueadoGlobal && comodines > 0 && (
                <div
                  className="q-alert"
                  style={{ background: "#fff1f2", borderColor: "#fecaca", color: "#991b1b" }}
                >
                  <b>🔒 Todas las preguntas están bloqueadas.</b>&nbsp;Usa{" "}
                  <b>Pista</b>, <b>Reintentar</b> para continuar.
                </div>
              )}

              {media ? <Media media={media} toAbsoluteUrl={toAbsoluteUrl} /> : null}

              <QuestionBody
                tipo={tipo}
                question={q}
                value={state.answer}
                onChange={(val) => dispatch({ type: ANSWER_CHANGE, value: val, question: q })}
                disabled={state.locked || bloqueadoGlobal}
                showHint={state.mostrarPista}
                hintText={hintText}
                allowedOptionIds={state.fiftyAllowed}
              />
            </div>

            {/* ================= Footer: comodines IZQ + acciones DER ================= */}
            <div className="q-footer">
              {state.mostrarPista && hasExplicacion && (
                <div className="q-alert success" style={{ marginBottom: "12px" }}>
                  <Lightbulb size={16} />
                  <div>
                    <b>Pista:</b> {hintText}
                  </div>
                </div>
              )}

              <div className="q-actions">
                <div className="q-comodWrap" ref={menuRef}>
                  <button
                    type="button"
                    className={`q-comodBtn ${ctaGlow ? "cta" : ""}`}
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-expanded={menuOpen}
                  >
                    <Coins size={18} />
                    Comodines
                    <span
                      className={`q-chip ${sinComodines ? "danger" : comodines === 1 ? "warn" : ""}`}
                      style={{ marginLeft: 6 }}
                    >
                      <b>{comodines}</b>
                    </span>
                  </button>

                  <ComodinesInline
                    open={menuOpen}
                    comodines={comodines}
                    allowHint={hasExplicacion}
                    showRetry={state.showRetry}
                    onPista={pedirPista}
                    onReintentar={reintentar}
                  />
                </div>

                <div className="q-actions-right">
                  <button
                    type="button"
                    className="q-btn"
                    onClick={handleSaltar}
                    title="Saltar esta actividad"
                  >
                    <SkipForward size={16} /> Saltar
                  </button>

                  <button
                    type="button"
                    className="q-primary"
                    disabled={!state.complete || state.locked}
                    onClick={handleCheck}
                  >
                    Comprobar
                  </button>
                </div>
              </div>
            </div>

            <div className="q-pad-bottom" />
          </div>
        </div>
      </div>

      {/* ===== Modal: saltar bloqueado ===== */}
      <BaseModal open={state.modals?.saltarBloqueado}>
        <div className="q-modalHead">ATENCIÓN</div>
        <div className="q-modalBody">
          <div
            className="q-modalIcon"
            style={{ background: "#fff7ed", borderColor: "#fed7aa", color: "#ea580c" }}
          >
            <AlertTriangle size={28} />
          </div>
          <div className="q-modalTitle">¿Saltar pregunta bloqueada?</div>
          <p className="q-modalText">
            Si saltas esta pregunta mientras está bloqueada, <b>todas las preguntas</b>{" "}
            quedarán bloqueadas hasta que uses un comodín.
          </p>
          <div className="q-modalNote">
            💡 Usa <b>Pista</b>, <b>Reintentar</b> para continuar sin bloquear todo.
          </div>
        </div>

        <div className="q-modalBtns">
          <button
            onClick={() => dispatch({ type: MODAL, name: "saltarBloqueado", open: false })}
            className="q-modalBtn"
          >
            Cancelar
          </button>
          <button onClick={confirmarSaltar} className="q-modalBtn danger">
            Saltar y bloquear todo
          </button>
        </div>
      </BaseModal>

      {/* ===== Modal: incorrecta (solo si AÚN quedan vidas) ===== */}
      <BaseModal open={state.modals?.incorrect}>
        <div className="q-modalHead">INTÉNTALO DE NUEVO</div>
        <div className="q-modalBody">
          <div
            className="q-modalIcon"
            style={{ background: "#fff7ed", borderColor: "#fed7aa", color: "#ea580c" }}
          >
            <AlertTriangle size={28} />
          </div>
          <div className="q-modalTitle">Respuesta incorrecta</div>
          <p className="q-modalText">
            Puedes volver a intentarlo. Si te quedas sin vidas, podrás usar <b>Comodines</b>{" "}
            (si tienes).
          </p>

          {state.mostrarPista && (
            <div className="q-alert success" style={{ marginTop: 10 }}>
              <Lightbulb size={16} />
              <div>
                <b>Pista:</b> {hintText}
              </div>
            </div>
          )}
        </div>

        <div className="q-modalBtns">
          <button
            onClick={() => dispatch({ type: MODAL, name: "incorrect", open: false })}
            className="q-modalBtn primary"
          >
            Entendido
          </button>
        </div>
      </BaseModal>
    </>
  );
}

/* ======================= QuestionBody y tipos ======================= */
function QuestionBody({
  tipo,
  question,
  value,
  onChange,
  disabled,
  showHint,
  hintText,
  allowedOptionIds,
}) {
  switch (tipo) {
    case "respuesta_corta":
      return (
        <RespuestaCorta
          value={value}
          onChange={onChange}
          disabled={disabled}
          onEnterCheckHint={showHint ? hintText : ""}
        />
      );

    case "seleccion_multiple":
      return (
        <SeleccionOpciones
          question={question}
          value={value}
          onChange={onChange}
          disabled={disabled}
          allowedOptionIds={allowedOptionIds}
        />
      );

    case "verdadero_falso": {
      const vfQ = {
        ...question,
        opciones: {
          opciones: [
            { id: "verdadero", texto: "Verdadero" },
            { id: "falso", texto: "Falso" },
          ],
        },
      };
      return (
        <SeleccionOpciones
          question={vfQ}
          value={value}
          onChange={onChange}
          disabled={disabled}
          forceSingle
        />
      );
    }

    case "completar":
      return (
        <Completar
          question={question}
          value={value}
          onChange={onChange}
          disabled={disabled}
          showHint={showHint}
          hintText={hintText}
        />
      );

    case "ordenamiento":
      return (
        <Ordenamiento
          question={question}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case "emparejamiento":
      return (
        <Emparejamiento
          question={question}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );

    default:
      return null;
  }
}

/* === Presentacionales === */
function RespuestaCorta({ value, onChange, disabled, onEnterCheckHint }) {
  return (
    <>
      <input
        className="q-input q-input--short"
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Escribe tu respuesta…"
        disabled={disabled}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
      {onEnterCheckHint ? (
        <div className="q-alert success" style={{ marginTop: 8 }}>
          <Lightbulb size={16} /> <b>Tip:</b> {onEnterCheckHint}
        </div>
      ) : null}
    </>
  );
}

function SeleccionOpciones({
  question,
  value,
  onChange,
  disabled,
  forceSingle = false,
  allowedOptionIds = null,
}) {
  const rawOpciones = question?.opciones?.opciones || [];
  const allowedSet = allowedOptionIds ? new Set(allowedOptionIds.map(String)) : null;

  const opciones = allowedSet ? rawOpciones.filter((o) => allowedSet.has(String(o.id))) : rawOpciones;

  const isSingle = forceSingle || (question?.respuesta_correcta?.correctas || []).length === 1;
  const compact =
    opciones.length <= 4 &&
    Math.max(0, ...opciones.map((o) => String(o.texto ?? "").length)) <= 12;
  const name = `opts-${question?.id || "q"}`;

  const toggle = (id) => {
    if (disabled) return;
    if (isSingle) onChange([id]);
    else {
      const has = (value || []).includes(id);
      onChange(has ? (value || []).filter((x) => x !== id) : [...(value || []), id]);
    }
  };

  return (
    <div className="q-optStack" style={{ opacity: disabled ? 0.55 : 1 }}>
      {compact ? (
        <div className="q-optGrid">
          {opciones.map((op) => (
            <button
              key={op.id}
              type="button"
              className={`q-pill ${(value || []).includes(op.id) ? "selected" : ""}`}
              aria-pressed={(value || []).includes(op.id)}
              onClick={() => toggle(op.id)}
              disabled={disabled}
            >
              <input
                type={isSingle ? "radio" : "checkbox"}
                name={name}
                checked={(value || []).includes(op.id)}
                readOnly
                hidden
              />
              <span className="q-optLabel">{op.texto}</span>
            </button>
          ))}
        </div>
      ) : (
        opciones.map((op) => (
          <label
            key={op.id}
            className={`q-optRow ${(value || []).includes(op.id) ? "selected" : ""}`}
            style={{ cursor: disabled ? "not-allowed" : "pointer" }}
          >
            <input
              type={isSingle ? "radio" : "checkbox"}
              name={name}
              checked={(value || []).includes(op.id)}
              onChange={() => toggle(op.id)}
              disabled={disabled}
            />
            <span className="q-optLabel">{op.texto}</span>
          </label>
        ))
      )}
    </div>
  );
}

function Completar({ question, value, onChange, disabled, showHint, hintText }) {
  const texto = question?.opciones?.texto_con_espacios || "";
  const correct = question?.respuesta_correcta?.respuestas || [];
  const blocks = useMemo(() => texto.split("_____"), [texto]);
  const blanks = value?.length || 0;

  return (
    <>
      <div
        style={{
          marginBottom: 4,
          color: "#374151",
          opacity: disabled ? 0.55 : 1,
          lineHeight: 1.7,
        }}
      >
        {blocks.map((chunk, i) => (
          <React.Fragment key={i}>
            <span style={{ fontSize: ".95rem" }}>{chunk}</span>
            {i < blanks && (
              <input
                className="q-input q-inline-input"
                style={{ margin: "0 6px 8px" }}
                type="text"
                value={value[i] || ""}
                size={Math.max(6, Math.min(24, correct[i]?.length || 10))}
                onChange={(e) =>
                  !disabled && onChange(value.map((x, idx) => (idx === i ? e.target.value : x)))
                }
                disabled={disabled}
                placeholder={`#${i + 1}`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {showHint && hintText ? (
        <div className="q-alert success">
          <Lightbulb size={16} />
          <div>
            <b>Pista:</b> {hintText}
          </div>
        </div>
      ) : null}
    </>
  );
}

function Ordenamiento({ question, value = [], onChange }) {
  const opciones = useMemo(() => extractOptionsList(question), [question]);

  const order = Array.isArray(value) ? value.map(String) : [];
  const effectiveOrder = order.length ? order : opciones.map((o) => String(o.id));

  const byId = useMemo(() => {
    const m = new Map();
    opciones.forEach((o) => m.set(String(o.id), o));
    return m;
  }, [opciones]);

  const move = (idx, dir) => {
    const next = [...effectiveOrder];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange?.(next);
  };

  if (!opciones.length) {
    return <div className="q-alert">No hay opciones para ordenar en esta pregunta.</div>;
  }

  return (
    <ol className="q-ordList">
      {effectiveOrder.map((id, idx) => {
        const item = byId.get(String(id));
        const texto = item?.texto ?? String(id);

        return (
          <li key={`${id}-${idx}`} className="q-ordRow">
            <span className="q-ordIndex">{idx + 1}</span>
            <div className="q-ordBox">{texto}</div>
            <div className="q-ordCtl">
              <button type="button" className="q-ordBtn" onClick={() => move(idx, -1)} disabled={idx === 0}>
                ↑
              </button>
              <button
                type="button"
                className="q-ordBtn"
                onClick={() => move(idx, +1)}
                disabled={idx === effectiveOrder.length - 1}
              >
                ↓
              </button>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Emparejamiento({ question, value, onChange, disabled }) {
  const colA = question?.opciones?.columna_a || [];
  const colB = question?.opciones?.columna_b || [];
  const [bOptions, setBOptions] = useState(() => shuffle(colB));

  useEffect(() => {
    setBOptions(shuffle(colB));
  }, [colB]);

  return (
    <div className="q-pairGrid" style={{ opacity: disabled ? 0.55 : 1 }}>
      <div className="q-pairHead">Columna A</div>
      <div className="q-pairHead">Columna B</div>

      {colA.map((a) => (
        <React.Fragment key={a.id}>
          <div className="q-pairLeft">{a.texto}</div>
          <div className="q-pairSelect">
            <select
              value={value[String(a.id)] || ""}
              onChange={(e) => !disabled && onChange({ ...value, [String(a.id)]: e.target.value })}
              disabled={disabled}
            >
              <option value="">Selecciona…</option>
              {bOptions.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.texto}
                </option>
              ))}
            </select>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

function shuffle(arr) {
  const s = [...arr];
  for (let i = s.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [s[i], s[j]] = [s[j], s[i]];
  }
  return s;
}
